import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { festivalsData } from '../../mocks/festivals-data';
import Navigation from '../../components/Navigation';

interface Festival {
  id: string;
  name: string;
  date: string;
  month: string;
  description: string;
  significance: string;
  rituals: string[];
  image: string;
  category: string;
}

export default function FestivalsPage() {
  const [searchParams] = useSearchParams();
  const [scrolled, setScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'calendar'>('grid');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [searchQuery, setSearchQuery] = useState('');
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [subscribeFormData, setSubscribeFormData] = useState({
    name: '',
    email: '',
    festivals: [] as string[]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [shareMessage, setShareMessage] = useState('');
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showFestivalsDropdown, setShowFestivalsDropdown] = useState(false);
  const navigate = useNavigate();

  // Read category from URL on mount
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  const categories = [
    { id: 'all', name: 'All Festivals' },
    { id: 'major', name: 'Major Festivals' },
    { id: 'spiritual', name: 'Spiritual' },
    { id: 'family', name: 'Family' },
    { id: 'auspicious', name: 'Auspicious Days' }
  ];

  const serviceCategories = [
    { name: 'Life Event Rituals', description: 'Naamkaran, Griha Pravesh, Mundan' },
    { name: 'Festival Pujas', description: 'Diwali, Ganesh Chaturthi, Navratri' },
    { name: 'Shradh Rituals', description: 'Ancestral ceremonies' },
    { name: 'Consult a Priest', description: 'Guidance & muhurat' }
  ];

  const festivalCategories = [
    { id: 'major', name: 'Major Festivals', count: festivalsData.filter(f => f.category === 'major').length },
    { id: 'spiritual', name: 'Spiritual', count: festivalsData.filter(f => f.category === 'spiritual').length },
    { id: 'family', name: 'Family', count: festivalsData.filter(f => f.category === 'family').length },
    { id: 'auspicious', name: 'Auspicious Days', count: festivalsData.filter(f => f.category === 'auspicious').length }
  ];

  const filteredFestivals = festivalsData
    .filter(f => selectedCategory === 'all' || f.category === selectedCategory)
    .filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()));

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.services-dropdown') && !target.closest('.festivals-dropdown')) {
        setShowServicesDropdown(false);
        setShowFestivalsDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleNavClick = (sectionId: string) => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleSubscribeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (subscribeFormData.festivals.length === 0) {
      alert('Please select at least one festival');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('name', subscribeFormData.name);
      formData.append('email', subscribeFormData.email);
      formData.append('festivals', subscribeFormData.festivals.join(', '));

      const response = await fetch('https://readdy.ai/api/form/d5st8dreef401v8jjddg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setSubscribeFormData({ name: '', email: '', festivals: [] });
        setTimeout(() => {
          setShowSubscribeModal(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFestivalSelection = (festivalName: string) => {
    setSubscribeFormData(prev => ({
      ...prev,
      festivals: prev.festivals.includes(festivalName)
        ? prev.festivals.filter(f => f !== festivalName)
        : [...prev.festivals, festivalName]
    }));
  };

  const handleShare = (platform: string, festival: Festival) => {
    const url = `${window.location.origin}/festival/${festival.id}`;
    const text = `Check out ${festival.name} on ${festival.date} ${festival.month}! ${festival.description}`;
    
    let shareUrl = '';
    
    switch(platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(festival.name)}&body=${encodeURIComponent(text + '\n\n' + url)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        setShareMessage('Link copied to clipboard!');
        setTimeout(() => setShareMessage(''), 3000);
        return;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  // Calendar view logic
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getMonthFromFestival = (festival: typeof festivalsData[0]) => {
    const monthStr = festival.month.toLowerCase().trim();
    return months.findIndex(m => monthStr.includes(m.toLowerCase()));
  };

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const currentYear = 2025;
  const daysInMonth = getDaysInMonth(selectedMonth, currentYear);
  const firstDay = getFirstDayOfMonth(selectedMonth, currentYear);

  const festivalsInMonth = filteredFestivals.filter(f => getMonthFromFestival(f) === selectedMonth);

  const getFestivalForDay = (day: number) => {
    return festivalsInMonth.find(f => {
      const festivalDay = parseInt(f.date.split('-')[0]);
      return festivalDay === day;
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <div className="pt-24 pb-12 sm:pb-16 bg-gradient-to-br from-[#FAF7F2] to-white px-4 sm:px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 border border-[#2E2E2E] rounded-full mb-6 sm:mb-8">
            <i className="ri-calendar-event-line text-xs sm:text-sm text-[#C78A2B]"></i>
            <span className="text-[10px] sm:text-xs tracking-widest text-[#2E2E2E] uppercase">Hindu Festival Calendar 2025</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-light text-[#2E2E2E] mb-4 sm:mb-6">
            Upcoming <span className="font-medium italic">Hindu Festivals</span>
          </h1>
          
          <p className="text-base sm:text-lg lg:text-xl text-[#3B2F2F] leading-relaxed max-w-3xl mx-auto font-light mb-6 sm:mb-8">
            Stay connected with sacred traditions. Book authentic rituals and pujas for upcoming festivals with experienced priests and complete transparency.
          </p>

          <button
            onClick={() => setShowSubscribeModal(true)}
            className="px-6 sm:px-8 py-2.5 sm:py-3 bg-[#C78A2B] text-white rounded-lg hover:bg-[#B8860B] transition-all inline-flex items-center gap-2 whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg text-sm sm:text-base"
          >
            <i className="ri-notification-line text-base sm:text-lg"></i>
            Subscribe to Festival Reminders
          </button>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="py-4 sm:py-6 px-4 sm:px-6 bg-white sticky top-[72px] sm:top-[88px] z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          {/* Search Bar */}
          <div className="mb-3 sm:mb-4">
            <div className="relative max-w-2xl mx-auto">
              <i className="ri-search-line absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base sm:text-lg"></i>
              <input
                type="text"
                placeholder="Search festivals by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-10 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-[#C78A2B] transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#C78A2B] cursor-pointer"
                >
                  <i className="ri-close-line text-lg sm:text-xl"></i>
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-2 lg:pb-0 w-full lg:w-auto scrollbar-hide">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex-shrink-0 ${
                    selectedCategory === category.id
                      ? 'bg-[#C78A2B] text-white'
                      : 'bg-gray-100 text-[#2E2E2E] hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2 bg-gray-100 rounded-full p-1 w-full lg:w-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex-1 lg:flex-none px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  viewMode === 'grid'
                    ? 'bg-white text-[#2E2E2E] shadow-sm'
                    : 'text-gray-600 hover:text-[#2E2E2E]'
                }`}
              >
                <i className="ri-grid-line mr-1 sm:mr-2"></i>
                Grid
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex-1 lg:flex-none px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  viewMode === 'calendar'
                    ? 'bg-white text-[#2E2E2E] shadow-sm'
                    : 'text-gray-600 hover:text-[#2E2E2E]'
                }`}
              >
                <i className="ri-calendar-line mr-1 sm:mr-2"></i>
                Calendar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Share Message Toast */}
      {shareMessage && (
        <div className="fixed top-20 sm:top-24 right-4 sm:right-6 z-50 bg-green-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in text-xs sm:text-sm">
          <i className="ri-checkbox-circle-line text-base sm:text-xl"></i>
          <span>{shareMessage}</span>
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
        <section className="py-8 sm:py-12 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            {filteredFestivals.length === 0 ? (
              <div className="text-center py-16 sm:py-20">
                <i className="ri-calendar-close-line text-5xl sm:text-6xl text-gray-300 mb-4"></i>
                <p className="text-lg sm:text-xl text-gray-500">No festivals found</p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-4 px-5 sm:px-6 py-2 bg-[#C78A2B] text-white rounded-lg hover:bg-[#B8860B] transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {filteredFestivals.map((festival) => (
                  <div key={festival.id} className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer">
                    {/* Festival Image */}
                    <div className="relative w-full h-[200px] sm:h-[240px]">
                      <img 
                        src={festival.image}
                        alt={festival.name}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute top-3 sm:top-4 right-3 sm:right-4 bg-white rounded-xl px-3 sm:px-4 py-2 sm:py-3 shadow-lg">
                        <div className="text-xl sm:text-2xl font-bold text-[#C78A2B] leading-none">{festival.date}</div>
                        <div className="text-[10px] sm:text-xs text-[#2E2E2E] mt-1">{festival.month}</div>
                      </div>
                    </div>

                    {/* Festival Content */}
                    <div className="p-4 sm:p-6">
                      <h3 className="text-lg sm:text-xl font-semibold text-[#2E2E2E] mb-2 sm:mb-3">{festival.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-3 sm:mb-4 font-light line-clamp-3">
                        {festival.description}
                      </p>

                      {/* Significance */}
                      <div className="mb-3 sm:mb-4">
                        <h4 className="text-xs sm:text-sm font-semibold text-[#2E2E2E] mb-2 flex items-center gap-2">
                          <i className="ri-star-line text-[#C78A2B]"></i>
                          Significance
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed font-light line-clamp-2">
                          {festival.significance}
                        </p>
                      </div>

                      {/* Rituals */}
                      <div className="mb-4 sm:mb-6">
                        <h4 className="text-xs sm:text-sm font-semibold text-[#2E2E2E] mb-2 flex items-center gap-2">
                          <i className="ri-ancient-gate-line text-[#C78A2B]"></i>
                          Key Rituals
                        </h4>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2">
                          {festival.rituals.slice(0, 3).map((ritual, index) => (
                            <span key={index} className="px-2 sm:px-3 py-1 bg-[#FAF7F2] text-[#2E2E2E] text-[10px] sm:text-xs rounded-full">
                              {ritual}
                            </span>
                          ))}
                          {festival.rituals.length > 3 && (
                            <span className="px-2 sm:px-3 py-1 bg-[#FAF7F2] text-[#2E2E2E] text-[10px] sm:text-xs rounded-full">
                              +{festival.rituals.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Share Buttons */}
                      <div className="mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] sm:text-xs text-gray-500 font-medium">Share with family:</span>
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare('whatsapp', festival);
                              }}
                              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-white flex items-center justify-center transition-colors cursor-pointer"
                              title="Share on WhatsApp"
                            >
                              <i className="ri-whatsapp-line text-xs sm:text-sm"></i>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare('facebook', festival);
                              }}
                              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#1877F2] hover:bg-[#0C63D4] text-white flex items-center justify-center transition-colors cursor-pointer"
                              title="Share on Facebook"
                            >
                              <i className="ri-facebook-fill text-xs sm:text-sm"></i>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare('twitter', festival);
                              }}
                              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#1DA1F2] hover:bg-[#0C8BD9] text-white flex items-center justify-center transition-colors cursor-pointer"
                              title="Share on Twitter"
                            >
                              <i className="ri-twitter-fill text-xs sm:text-sm"></i>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare('copy', festival);
                              }}
                              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-200 hover:bg-gray-300 text-[#2E2E2E] flex items-center justify-center transition-colors cursor-pointer"
                              title="Copy Link"
                            >
                              <i className="ri-link text-xs sm:text-sm"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* CTA Buttons */}
                      <div className="flex gap-2 sm:gap-3">
                        <Link 
                          to={`/festival/${festival.id}`}
                          className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-[#C78A2B] text-[#C78A2B] rounded-lg text-xs sm:text-sm font-medium hover:bg-[#C78A2B] hover:text-white transition-all inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                        >
                          Learn More
                        </Link>
                        <Link 
                          to="/book-ritual"
                          className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#C78A2B] text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-[#B8860B] transition-all inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                        >
                          Book Ritual
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <section className="py-8 sm:py-12 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            {/* Month Selector */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-md mb-6 sm:mb-8">
              <div className="flex items-center justify-between mb-6 sm:mb-8">
                <button
                  onClick={() => setSelectedMonth((selectedMonth - 1 + 12) % 12)}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-100 hover:bg-[#C78A2B] hover:text-white transition-colors flex items-center justify-center cursor-pointer flex-shrink-0"
                >
                  <i className="ri-arrow-left-s-line text-xl sm:text-2xl"></i>
                </button>
                <h2 className="text-xl sm:text-2xl lg:text-4xl font-light text-[#2E2E2E] text-center">
                  {months[selectedMonth]} {currentYear}
                </h2>
                <button
                  onClick={() => setSelectedMonth((selectedMonth + 1) % 12)}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-100 hover:bg-[#C78A2B] hover:text-white transition-colors flex items-center justify-center cursor-pointer flex-shrink-0"
                >
                  <i className="ri-arrow-right-s-line text-xl sm:text-2xl"></i>
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 sm:gap-2 lg:gap-4">
                {/* Day Headers */}
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="text-center font-semibold text-[#2E2E2E] py-2 text-[10px] sm:text-xs lg:text-base">
                    {day}
                  </div>
                ))}

                {/* Empty cells for days before month starts */}
                {Array.from({ length: firstDay }).map((_, index) => (
                  <div key={`empty-${index}`} className="aspect-square"></div>
                ))}

                {/* Calendar Days */}
                {Array.from({ length: daysInMonth }).map((_, index) => {
                  const day = index + 1;
                  const festival = getFestivalForDay(day);
                  
                  return (
                    <div
                      key={day}
                      className={`aspect-square rounded-lg lg:rounded-xl p-1 sm:p-2 lg:p-3 transition-all ${
                        festival
                          ? 'bg-[#C78A2B] text-white cursor-pointer hover:shadow-lg hover:-translate-y-1'
                          : 'bg-gray-50 text-[#2E2E2E]'
                      }`}
                    >
                      <div className="text-xs sm:text-sm lg:text-lg font-semibold mb-0.5 sm:mb-1">{day}</div>
                      {festival && (
                        <Link to={`/festival/${festival.id}`} className="block">
                          <div className="text-[8px] sm:text-[10px] lg:text-xs font-medium leading-tight line-clamp-2">
                            {festival.name}
                          </div>
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Festivals in Selected Month */}
            {festivalsInMonth.length > 0 ? (
              <div>
                <h3 className="text-xl sm:text-2xl font-semibold text-[#2E2E2E] mb-4 sm:mb-6">
                  Festivals in {months[selectedMonth]}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {festivalsInMonth.map((festival) => (
                    <Link
                      key={festival.id}
                      to={`/festival/${festival.id}`}
                      className="bg-white rounded-xl p-4 sm:p-6 shadow-md hover:shadow-xl transition-all cursor-pointer"
                    >
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="bg-[#C78A2B] rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white text-center flex-shrink-0">
                          <div className="text-xl sm:text-2xl font-bold leading-none">{festival.date}</div>
                          <div className="text-[10px] sm:text-xs mt-1">{festival.month}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base sm:text-lg font-semibold text-[#2E2E2E] mb-1 sm:mb-2">{festival.name}</h4>
                          <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{festival.description}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-8 sm:p-12 shadow-md text-center">
                <i className="ri-calendar-close-line text-5xl sm:text-6xl text-gray-300 mb-4"></i>
                <p className="text-lg sm:text-xl text-gray-500">No festivals in {months[selectedMonth]}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Subscribe Modal */}
      {showSubscribeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 sm:p-6">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between">
              <h2 className="text-lg sm:text-2xl font-semibold text-[#2E2E2E]">Subscribe to Festival Reminders</h2>
              <button
                onClick={() => setShowSubscribeModal(false)}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-lg sm:text-xl text-[#2E2E2E]"></i>
              </button>
            </div>

            <form id="festival-reminders-form" data-readdy-form onSubmit={handleSubscribeSubmit} className="p-4 sm:p-6">
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                Get notified about upcoming festivals and never miss an important celebration. Select the festivals you'd like to receive reminders for.
              </p>

              <div className="mb-4 sm:mb-6">
                <label className="block text-xs sm:text-sm font-semibold text-[#2E2E2E] mb-2">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={subscribeFormData.name}
                  onChange={(e) => setSubscribeFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-[#C78A2B] transition-colors"
                  placeholder="Enter your name"
                />
              </div>

              <div className="mb-4 sm:mb-6">
                <label className="block text-xs sm:text-sm font-semibold text-[#2E2E2E] mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={subscribeFormData.email}
                  onChange={(e) => setSubscribeFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-[#C78A2B] transition-colors"
                  placeholder="Enter your email"
                />
              </div>

              <div className="mb-4 sm:mb-6">
                <label className="block text-xs sm:text-sm font-semibold text-[#2E2E2E] mb-3">
                  Select Festivals <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 max-h-64 overflow-y-auto p-2">
                  {festivalsData.map((festival) => (
                    <label
                      key={festival.id}
                      className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 border-2 border-gray-200 rounded-lg hover:border-[#C78A2B] transition-colors cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={subscribeFormData.festivals.includes(festival.name)}
                        onChange={() => toggleFestivalSelection(festival.name)}
                        className="mt-0.5 sm:mt-1 w-4 h-4 text-[#C78A2B] border-gray-300 rounded focus:ring-[#C78A2B] cursor-pointer flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs sm:text-sm font-medium text-[#2E2E2E]">{festival.name}</div>
                        <div className="text-[10px] sm:text-xs text-gray-500">{festival.date} {festival.month}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 sm:gap-3">
                  <i className="ri-checkbox-circle-line text-xl sm:text-2xl text-green-600 flex-shrink-0"></i>
                  <p className="text-xs sm:text-sm text-green-800">Successfully subscribed! You'll receive reminders for selected festivals.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 sm:gap-3">
                  <i className="ri-error-warning-line text-xl sm:text-2xl text-red-600 flex-shrink-0"></i>
                  <p className="text-xs sm:text-sm text-red-800">Something went wrong. Please try again.</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => setShowSubscribeModal(false)}
                  className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#C78A2B] text-white rounded-lg hover:bg-[#B8860B] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-[#2E2E2E]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-light text-white mb-6 sm:mb-8">
            Never Miss a Sacred Occasion
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed mb-8 sm:mb-12 font-light">
            Book your festival rituals in advance and ensure authentic ceremonies performed with devotion and transparency.
          </p>
          <Link 
            to="/book-ritual"
            className="px-8 sm:px-12 py-3 sm:py-4 bg-[#C78A2B] text-[#2E2E2E] rounded-full text-base sm:text-lg font-medium hover:bg-[#B8860B] transition-all hover:shadow-xl whitespace-nowrap cursor-pointer inline-block"
          >
            Book a Festival Ritual
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black rounded-t-[60px] pt-16 sm:pt-20 pb-10 sm:pb-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-16 mb-16 sm:mb-20">
            <div className="lg:col-span-5">
              <Link to="/" className="flex items-center gap-3 mb-4 sm:mb-6 cursor-pointer">
                <img src="https://public.readdy.ai/ai/img_res/15313995-b9c8-4137-afe3-659f58e4dd9d.png" alt="EkDharma Logo" className="h-8 w-8 sm:h-10 sm:w-10 object-contain" />
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-light text-white">EkDharma</h3>
              </Link>
              <p className="text-base sm:text-lg text-gray-400 leading-relaxed mb-8 sm:mb-12 font-light">
                Connecting devotees worldwide with authentic Sanatana Dharma traditions.
              </p>
            </div>

            <div className="lg:col-span-3">
              <h4 className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider mb-5 sm:mb-7">Quick Links</h4>
              <div className="space-y-4 sm:space-y-5">
                <Link to="/#services" className="block text-white text-sm sm:text-base hover:text-[#C78A2B] transition-colors cursor-pointer">Our Services</Link>
                <Link to="/#why-us" className="block text-white text-sm sm:text-base hover:text-[#C78A2B] transition-colors cursor-pointer">Why Choose Us</Link>
                <Link to="/festivals" className="block text-white text-sm sm:text-base hover:text-[#C78A2B] transition-colors cursor-pointer">Festivals</Link>
                <Link to="/book-ritual" className="block text-white text-sm sm:text-base hover:text-[#C78A2B] transition-colors cursor-pointer">Book Ritual</Link>
              </div>
            </div>

            <div className="lg:col-span-4">
              <h4 className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider mb-4 sm:mb-5">Connect</h4>
              <p className="text-white text-base sm:text-lg mb-3 sm:mb-4">+91 98XXXXXXXX</p>
              <p className="text-white text-base sm:text-lg mb-8 sm:mb-10">contact@ekdharma.com</p>
              
              <h4 className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider mb-3">Location</h4>
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                Noida, UP<br />
                India
              </p>
            </div>
          </div>

          <div className="pt-8 sm:pt-12 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-8">
            <div className="flex items-center gap-6 sm:gap-8">
              <a href="#" className="text-white text-xs sm:text-sm hover:text-[#C78A2B] transition-colors cursor-pointer">Instagram</a>
              <a href="#" className="text-white text-xs sm:text-sm hover:text-[#C78A2B] transition-colors cursor-pointer">Facebook</a>
              <a href="#" className="text-white text-xs sm:text-sm hover:text-[#C78A2B] transition-colors cursor-pointer">YouTube</a>
            </div>
            
            <p className="text-gray-500 text-xs sm:text-sm text-center">© 2025 EkDharma. All rights reserved.</p>
            
            <div className="flex items-center gap-6 sm:gap-8">
              <a href="#" className="text-white text-xs sm:text-sm hover:text-[#C78A2B] transition-colors cursor-pointer">Privacy Policy</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white text-xs sm:text-sm hover:text-[#C78A2B] transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
